/**
 * SuperAdminPanel — atoms_admin only.
 * Notion-inspired full-height sidebar layout.
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  56px navy header  [ATOMS Logo]  Super Admin  [ATOMS Admin badge] │
 * ├────────────────┬─────────────────────────────────────────────────┤
 * │  220px sidebar │  Scrollable content                              │
 * │                │                                                  │
 * │  SYSTEM        │  <OverviewTab / SuperOrgsTab / …>                │
 * │  📊 Overview   │                                                  │
 * │  🏢 Orgs   ◀  │                                                  │
 * │  ⚙ Machines   │                                                  │
 * │  👥 Users      │                                                  │
 * └────────────────┴─────────────────────────────────────────────────┘
 */
import React, { useEffect, useState } from 'react';
import { Image, Alert, Badge, ActionIcon } from '@mantine/core';
import {
  MdSpaceDashboard, MdBusiness, MdPrecisionManufacturing,
  MdPeople, MdArrowBack,
} from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import AtomsLogo from '../../assets/AtomsLogo.png';
import OverviewTab      from './OverviewTab';
import SuperOrgsTab     from './SuperOrgsTab';
import SuperMachinesTab from './SuperMachinesTab';
import SuperUsersTab    from './SuperUsersTab';

const ATOMS_BLUE    = '#1F4E79';
const ATOMS_ACTIVE  = '#1F4E7918'; // translucent navy for active bg

// ── Nav item ──────────────────────────────────────────────────────────────────

const NavItem = ({ icon: Icon, label, value, active, onClick }) => (
  <div
    onClick={() => onClick(value)}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '9px 12px',
      borderRadius: 7,
      cursor: 'pointer',
      background: active ? ATOMS_ACTIVE : 'transparent',
      color: active ? ATOMS_BLUE : '#4b5563',
      fontWeight: active ? 600 : 400,
      fontSize: 14,
      marginBottom: 2,
      userSelect: 'none',
      transition: 'background 0.1s, color 0.1s',
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f3f4f6'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    <Icon size={17} style={{ flexShrink: 0 }} />
    {label}
  </div>
);

// ── SuperAdminPanel ───────────────────────────────────────────────────────────

const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role     = localStorage.getItem('role') || 'user';

  const [activeTab,  setActiveTab]  = useState(location.state?.tab || 'overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (role !== 'atoms_admin') {
      navigate('/app/dashboard', { replace: true });
      return;
    }
    // If returning from a wizard with refresh flag, remount the active tab
    if (location.state?.refresh) {
      if (location.state?.tab) setActiveTab(location.state.tab);
      setRefreshKey((k) => k + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (role !== 'atoms_admin') return null;

  const navItems = [
    { value: 'overview',  label: 'Overview',       icon: MdSpaceDashboard },
    { value: 'orgs',      label: 'Organisations',  icon: MdBusiness },
    { value: 'machines',  label: 'Machines',        icon: MdPrecisionManufacturing },
    { value: 'users',     label: 'Users',           icon: MdPeople },
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f7f8f9', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{
        background: ATOMS_BLUE,
        color: 'white',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        gap: 14,
        flexShrink: 0,
      }}>
        <ActionIcon
          variant="transparent"
          onClick={() => navigate('/app/dashboard')}
          title="Back to App"
          sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' } }}
        >
          <MdArrowBack size={20} />
        </ActionIcon>
        <Image src={AtomsLogo} width={30} height={30} fit="contain" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'white', lineHeight: 1.2 }}>
            ATOMS Super Admin
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 1 }}>
            Full system access — all organisations, machines &amp; users
          </div>
        </div>
        <Badge
          size="sm"
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            fontWeight: 600,
            letterSpacing: '0.03em',
          }}
        >
          ATOMS Admin
        </Badge>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        <div style={{
          width: 220,
          background: '#ffffff',
          borderRight: '1px solid #e8ecf0',
          padding: '24px 12px 20px',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            padding: '0 10px',
            marginBottom: 8,
          }}>
            System
          </div>

          {navItems.map((item) => (
            <NavItem
              key={item.value}
              {...item}
              active={activeTab === item.value}
              onClick={setActiveTab}
            />
          ))}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Footer */}
          <div
            onClick={() => navigate('/app/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 12px',
              borderRadius: 7,
              cursor: 'pointer',
              fontSize: 13,
              color: '#9ca3af',
              marginTop: 8,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#4b5563'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
          >
            <MdArrowBack size={15} />
            Back to App
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
          {error && <Alert color="red" mb="lg">{error}</Alert>}

          {activeTab === 'overview'  && <OverviewTab      key={`overview-${refreshKey}`} />}
          {activeTab === 'orgs'      && <SuperOrgsTab     key={`orgs-${refreshKey}`} />}
          {activeTab === 'machines'  && <SuperMachinesTab key={`machines-${refreshKey}`} />}
          {activeTab === 'users'     && <SuperUsersTab    key={`users-${refreshKey}`} />}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
